import React, { useEffect, useState, useRef } from "react";
import {
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  CreditCard,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import curebaylogo from "../assets/curebaylogo.svg";
import CartDropdown from "../components/CartDropdown";
import { APP_ROUTES } from "../constants/approutes";
import { useNavigate } from "react-router-dom";
import OurLayouts from "./layout/index";
import { getHospitalDetails } from "../utils/hospitalStorage";

export default function Header({
  toggleSidebar,
  cartCount,
  onCartClick,
  patientId,
}) {
  const disabledFeatures = JSON.parse(localStorage.getItem("disabledFeatures") || "[]");
  const [firstLetter, setFirstLetter] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showOrderTrackingSubmenu, setShowOrderTrackingSubmenu] =
    useState(false);
  const [clinicLogo, setClinicLogo] = useState(curebaylogo);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [hospitalDetails, setHospitalDetails] = useState(null);
  const loginUser = localStorage.getItem("user") || {};
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const tenantId = localStorage.getItem("tenantId") || "";
  const API_BASE = process.env.REACT_APP_API_URL;


  

  const buildLogoUrl = (value) => {
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    const baseUrl = API_BASE || process.env.REACT_APP_API_URL || "";
    const sep = baseUrl && baseUrl.endsWith("/") ? "" : "/";
    const fileParam = encodeURIComponent(value);
    return `${baseUrl}${sep}patient-medical-history/file-url?fileName=${fileParam}`;
  };

  const loadLogoPreviewWithAuth = async (fileName) => {
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      if (!fileName || !token || !tenantId) return;
      const url = buildLogoUrl(fileName);
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
          Accept: "*/*",
        },
        redirect: "follow",
      });
      if (!res.ok) return;
      // Some APIs return a signed URL (text) while others may redirect to the file
      // Handle both: if content-type is JSON/text, read as text; if image/blob, create object URL
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        try {
          const data = await res.json();
          if (data && typeof data.signedUrl === "string" && /^https?:\/\//i.test(data.signedUrl)) {
            setLogoPreviewUrl(data.signedUrl);
            // Cache the URL for future use
            localStorage.setItem("cachedClinicLogoUrl", data.signedUrl);
            return;
          }
        } catch (_) {
          // fall through to blob handling
        }
      } else if (contentType.includes("text/")) {
        const text = await res.text();
        if (/^https?:\/\//i.test(text)) {
          setLogoPreviewUrl(text);
          // Cache the URL for future use
          localStorage.setItem("cachedClinicLogoUrl", text);
          return;
        }
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setLogoPreviewUrl(objectUrl);
      // Cache the blob URL (though it's temporary, it helps with immediate loading)
      localStorage.setItem("cachedClinicLogoUrl", objectUrl);
    } catch (e) {
      console.error("Failed to load logo preview:", e);
    }
  };

  useEffect(() => {
    const firstName = localStorage.getItem("firstName") || "";
    setFirstLetter(firstName.charAt(0).toUpperCase());

    const hospital = getHospitalDetails();
    setHospitalDetails(hospital);

    const storedClinicLogo = localStorage.getItem("clinilogo");
    const storedClinicName = localStorage.getItem("clinicname");

    const displayName =
      hospital?.firstName ||
      hospital?.hospitalName ||
      hospital?.name ||
      (typeof storedClinicName === "string" &&
      storedClinicName.trim() &&
      storedClinicName !== "null"
        ? storedClinicName
        : "");

    if (displayName) {
      setClinicName(displayName);
    }

    const logoFile =
      hospital?.hospitalLogoUrl ||
      hospital?.logourl ||
      hospital?.logoUrl ||
      hospital?.hospitalLogo ||
      storedClinicLogo;

    const isValidLogoValue =
      typeof logoFile === "string" &&
      logoFile.trim() !== "" &&
      logoFile !== "null" &&
      logoFile !== "undefined";

    if (isValidLogoValue) {
      loadLogoPreviewWithAuth(logoFile);
      setClinicLogo(logoFile);
    }

    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
        setShowOrderTrackingSubmenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCartClick = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
    if (onCartClick) {
      onCartClick();
    }
  };

  const toggleProfileDropdown = (e) => {
    e.stopPropagation();
    setShowProfileDropdown(!showProfileDropdown);
    setShowOrderTrackingSubmenu(false);
  };

  const toggleOrderTrackingSubmenu = (e) => {
    e.stopPropagation();
    setShowOrderTrackingSubmenu(!showOrderTrackingSubmenu);
  };
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");

      await fetch(`${process.env.REACT_APP_API_URL}auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
      });
      localStorage.removeItem("auth_token");
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };
  const handleMenuItemClick = (action) => {
    console.log(action);
    setShowProfileDropdown(false);
    setShowOrderTrackingSubmenu(false);

    switch (action) {
      case "logout":
        handleLogout();
        break;
      case "clinic-details":
        navigate(APP_ROUTES.MYPROFILE);
        break;
      case "admin-profile":
        navigate(APP_ROUTES.ADMIN_PROFILE);
        break;
      case "pharmacy-orders":
        navigate("/ClinicMedicineDetails", {});
        break;
      case "lab-radiology":
        navigate("/ClinicRadiologyDetails", {});
        break;
      case "lab-pathology":
        navigate("/ClinicDiagnosticDetails", {});
        break;
      case "surgery-orders":
        navigate(APP_ROUTES.CLINICSURGERYDETAILS, {});
        break;
      case "ambulance-orders":
        navigate(APP_ROUTES.CLINICAMBULANCEDETAILS, {});
        break;
      case "change-password":
        navigate(APP_ROUTES.CHANGE_PASSWORD);
        break;
      case "feedback":
        navigate(APP_ROUTES.FEEDBACK);
        break;
      default:
        break;
    }
  };
  const hospitalTitle = (() => {
    const name = clinicName || hospitalDetails?.firstName || hospitalDetails?.hospitalName || "";
    const city = hospitalDetails?.city;
    const state = hospitalDetails?.state;
    if (!name) return "";
    if (city && !name.toLowerCase().includes(city.toLowerCase())) {
      return `${name}, ${city} , ${state}`;
    }
    if (state && !name.toLowerCase().includes(state.toLowerCase())) {
      return `${name}, ${state}`;
    }
    return name;
  })();

  const locationLabel = [hospitalDetails?.address]
    .filter(Boolean)
    .join(", ");

  const formatCategory = (value) => {
    if (!value) return "";
    return String(value)
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <header className="sticky top-0 z-50 flex items-center justify-between bg-white px-4 md:px-8 py-2.5 shadow-sm border-b border-slate-200">
      <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
        <button
          type="button"
          onClick={toggleSidebar}
          className="md:hidden text-gray-700 hover:text-gray-900 transition shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-7 h-7"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 5.25h16.5m-16.5 6.75h16.5m-16.5 6.75h16.5"
            />
          </svg>
        </button>

        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            <div className="w-12 h-12 rounded-lg border border-teal-200 bg-white flex items-center justify-center overflow-hidden shadow-sm">
              {logoPreviewUrl ? (
                <img
                  src={logoPreviewUrl}
                  alt="Hospital logo"
                  className="w-full h-full object-contain p-1"
                />
              ) : (
                <img
                  src={curebaylogo}
                  alt="Logo"
                  className="w-8 h-8 object-contain opacity-80"
                />
              )}
            </div>
            {hospitalDetails && (
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                <BadgeCheck size={10} className="text-white" strokeWidth={3} />
              </span>
            )}
          </div>

          {(hospitalTitle || hospitalDetails) && (
            <div className="min-w-0 hidden sm:block">
              {hospitalTitle && (
                <h1 className="text-[15px] md:text-base font-bold leading-tight truncate mb-1 bg-gradient-to-r from-[#004376] to-[#00BA7E] bg-clip-text text-transparent">
                  {hospitalTitle}
                </h1>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                {/* {hospitalDetails?.userId && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-medium text-slate-600">
                    <CreditCard size={11} className="text-slate-400 shrink-0" />
                    <span className="truncate max-w-[120px]">ID: {hospitalDetails.userId}</span>
                  </span>
                )} */}
                {locationLabel && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-medium text-slate-600">
                    <MapPin size={11} className="text-teal-600 shrink-0" />
                    <span className="truncate max-w-[250px]">{locationLabel}</span>
                  </span>
                )}
                {/* {hospitalDetails?.categoryType && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 border border-teal-200 text-[11px] font-semibold text-teal-800">
                    <span className="w-1.5 h-1.5 rounded-sm bg-teal-500 shrink-0" />
                    {formatCategory(hospitalDetails.categoryType)}
                  </span>
                )}
                {hospitalDetails?.gst && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] font-semibold text-amber-800">
                    <ShieldCheck size={11} className="text-amber-600 shrink-0" />
                    GST Registered
                  </span>
                )} */}
              </div>
            </div>
          )}

          {hospitalTitle && (
            <p className="sm:hidden text-sm font-bold truncate max-w-[140px] bg-gradient-to-r from-[#004376] to-[#00BA7E] bg-clip-text text-transparent">
              {hospitalTitle}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div
          className="relative"
          ref={profileRef}
          onClick={toggleProfileDropdown}
        >
          <div className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#004172] to-[#3cba84] text-white flex items-center justify-center text-lg font-semibold shadow-md">
              {firstLetter}
            </div>
            <ChevronDown
              className={`text-gray-500 w-5 h-5 transition-transform ${
                showProfileDropdown ? "rotate-180" : ""
              }`}
            />
          </div>

          {showProfileDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-gradient-to-r from-[#004172] to-[#3cba84] rounded-md shadow-lg py-1 z-50 border border-gray-200">
             
             <button
                onClick={() => handleMenuItemClick("admin-profile")}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
              >
               {role === "ADMIN" ? "Admin Profile" : "My Profile"}
              </button>
              {tenantId !== "CureBay" && (
              <button
                onClick={() => handleMenuItemClick("clinic-details")}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
              >
                Organization Details
              </button>
              )}
              
              {role === "USER" && !disabledFeatures.includes("schedule-service") && (
                <div className="relative">
                  <button
                    onClick={toggleOrderTrackingSubmenu}
                    className="flex items-center justify-between w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
                  >
                    <span>Order Tracking</span>
                    <ChevronLeft
                      className={`w-4 h-4 transition-transform ${
                        showOrderTrackingSubmenu ? "-rotate-90" : ""
                      } text-white`}
                    />
                  </button>

                  {showOrderTrackingSubmenu && (
                    <div className="absolute right-full top-0 mr-1 w-48 bg-gradient-to-r from-[#004172] to-[#3cba84] rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <button
                        onClick={() => handleMenuItemClick("pharmacy-orders")}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
                      >
                        Pharmacy Orders
                      </button>
                      <button
                        onClick={() => handleMenuItemClick("lab-radiology")}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
                      >
                        Lab Radiology
                      </button>
                      <button
                        onClick={() => handleMenuItemClick("lab-pathology")}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
                      >
                        Lab Pathology
                      </button>
                      <button
                        onClick={() => handleMenuItemClick("surgery-orders")}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
                      >
                        Surgery Orders
                      </button>
                      <button
                        onClick={() => handleMenuItemClick("ambulance-orders")}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
                      >
                        Ambulance Orders
                      </button>
                    </div>
                  )}
                </div>
              )}
{tenantId !== "CureBay" && (
  <>
              <button
                onClick={() => handleMenuItemClick("change-password")}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
              >
                Change Password
              </button>
              <button
                onClick={() => handleMenuItemClick("feedback")}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/20"
              >
                Feedback
              </button>
              {/* <div className="border-t border-white/20 my-1"></div> */}
              {/* <button
                onClick={() => handleMenuItemClick("logout")}
                className="block w-full text-left px-4 py-2 text-sm font-medium text-white hover:bg-red-600 hover:bg-opacity-80"
              >
                Logout
              </button> */}
              </>
              )}
               <div className="border-t border-white/20 my-1"></div>
              <button
                onClick={() => handleMenuItemClick("logout")}
                className="block w-full text-left px-4 py-2 text-sm font-medium text-white hover:bg-red-600 hover:bg-opacity-80"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
